/**
 * Coarse relative-time formatters — pure JS, no dayjs dependency.
 * Shared by the promotion surfaces (pipeline cards, detail timeline,
 * audit trail, create-modal subheader, tenant version card) so every
 * timestamp reads the same way ("2m ago", not a locale dump).
 */

/** "x seconds/minutes/hours/days ago" for a past ISO timestamp. */
export const timeAgo = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h ago`;
  return `${Math.round(deltaSec / 86400)}d ago`;
};

/** Compact elapsed duration since an ISO timestamp ("45s", "12m", "3h 20m"). */
export const elapsedSince = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s`;
  if (deltaSec < 3600) {
    const m = Math.floor(deltaSec / 60);
    const s = deltaSec % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(deltaSec / 3600);
  const m = Math.round((deltaSec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/** Milliseconds after which an in-flight step should read as "taking longer than usual". */
export const STUCK_THRESHOLD_MS = 10 * 60 * 1000;

/** True when an in-flight step started long enough ago to warrant a slow-warning. */
export const isLikelyStuck = (sinceIso: string | null | undefined): boolean => {
  if (!sinceIso) return false;
  const then = new Date(sinceIso).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then >= STUCK_THRESHOLD_MS;
};
